import { useEffect, useRef, useState } from "react";

import {
  platform,
  type DeviceShare,
  type MoveResult,
  type PlatformDevice,
  type PlatformOrg,
} from "../lib/api.js";
import { OrgUserTree } from "../components/OrgUserTree.js";
import { useAuth } from "../lib/auth-context.js";
import { shareLabel } from "../lib/share-label.js";
import { useLiveData } from "../lib/use-live-data.js";

/**
 * Platform administration.
 *
 * Deliberately plain and slightly unwelcoming. Everything on this page crosses
 * an organization boundary, which the rest of the product exists to prevent,
 * so it should not feel like an ordinary settings screen.
 */

const REFRESH_MS = 120_000;

export function PlatformPage() {
  const { user } = useAuth();
  const { data: devices, error, reload } = useLiveData(() => platform.devices(), REFRESH_MS);
  const { data: orgs } = useLiveData(() => platform.organizations(), REFRESH_MS);
  const { data: audit, reload: reloadAudit } = useLiveData(() => platform.audit(), REFRESH_MS);

  const [moving, setMoving] = useState<string | null>(null);
  const [destination, setDestination] = useState<Record<string, string>>({});
  const [result, setResult] = useState<MoveResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // The API returns 404 rather than 403 to a non-platform-admin, so this page
  // says the same thing rather than implying there is something to be let into.
  if (!user?.isPlatformAdmin) {
    return <p style={{ color: "var(--text-muted)" }}>NOT FOUND</p>;
  }

  const move = async (device: PlatformDevice) => {
    const target = destination[device.id];
    if (!target) return;
    const org = orgs?.organizations.find((o) => o.id === target);

    if (
      !confirm(
        `Move "${device.name}" from ${device.tenant_name} to ${org?.name ?? "that organization"}?\n\n` +
          `Its entire metric history moves with it. ${device.tenant_name} will no longer be able to see ` +
          `the device or any of its past data, and any alert rules they wrote for it are deleted.`,
      )
    ) {
      return;
    }

    setMoving(device.id);
    setActionError(null);
    setResult(null);
    try {
      setResult(await platform.moveDevice(device.id, target));
      reload();
      reloadAudit();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Move failed");
    } finally {
      setMoving(null);
    }
  };

  return (
    <div style={{ display: "grid", gap: 28 }}>
      <section>
        <h1 style={heading}>&gt; PLATFORM ADMINISTRATION</h1>
        <p style={help}>
          Router Report level, above every organization. Actions here cross
          organization boundaries and are recorded in the log at the bottom of
          this page.
        </p>

        {actionError && <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${actionError}`}</p>}

        {result && (
          <div style={{ ...card, borderColor: "var(--success-text)", marginBottom: 14, fontSize: 13 }}>
            <div style={{ color: "var(--success-text)", letterSpacing: "0.1em", marginBottom: 6 }}>
              MOVED {result.deviceName.toUpperCase()}
            </div>
            <div style={{ color: "var(--text-secondary)" }}>
              {result.fromTenantName} &rarr; {result.toTenantName}
            </div>
            <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 6, lineHeight: 1.6 }}>
              {Object.entries(result.rowsMoved)
                .filter(([, n]) => n > 0)
                .map(([table, n]) => `${table} ${n}`)
                .join(" · ") || "no history rows"}
              {result.rulesDeleted > 0 && ` · ${result.rulesDeleted} alert rule(s) deleted`}
              {result.eventsDeleted > 0 && ` · ${result.eventsDeleted} alert event(s) deleted`}
            </div>
          </div>
        )}

        <h2 style={{ ...heading, fontSize: 13 }}>&gt; ALL DEVICES</h2>
        {error && !devices && <p style={{ color: "var(--status-critical)" }}>{`!! ${error}`}</p>}
        {!devices && !error && <p style={{ color: "var(--text-muted)" }}>LOADING...</p>}

        {devices && (
          <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
                  {["DEVICE", "ADDRESS", "ORGANIZATION", "SHARED WITH", "MOVE TO"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {devices.devices.map((device) => (
                  <tr key={device.id} style={{ borderBottom: "1px solid var(--gridline)" }}>
                    <td style={td}>{device.name}</td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{device.address}</td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{device.tenant_name}</td>
                    <td style={td}>
                      <ShareCell device={device} organizations={orgs?.organizations ?? []} onChange={reload} />
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <select
                        value={destination[device.id] ?? ""}
                        onChange={(e) =>
                          setDestination((d) => ({ ...d, [device.id]: e.target.value }))
                        }
                        style={select}
                      >
                        <option value="">-- CHOOSE --</option>
                        {(orgs?.organizations ?? [])
                          // Its current owner is not a destination.
                          .filter((o) => o.id !== device.tenant_id)
                          .map((o) => (
                            <option key={o.id} value={o.id}>{o.name.toUpperCase()}</option>
                          ))}
                      </select>{" "}
                      <button
                        disabled={!destination[device.id] || moving === device.id}
                        onClick={() => void move(device)}
                        style={{
                          ...smallButton,
                          color: destination[device.id] ? "var(--status-warning)" : "var(--text-muted)",
                        }}
                      >
                        {moving === device.id ? "[ MOVING... ]" : "[ MOVE ]"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 style={{ ...heading, fontSize: 13 }}>&gt; ORGANIZATIONS AND PEOPLE</h2>
        <p style={help}>
          Every organization on the platform and who is in it. Accounts in more
          than one are marked, as are accounts in none — which appear nowhere
          else, and cannot sign in.
        </p>
        <OrgUserTree />
      </section>

      <section>
        <h2 style={{ ...heading, fontSize: 13 }}>&gt; AUDIT LOG</h2>
        <p style={help}>Append-only. Nothing in the application deletes from this.</p>
        {audit && audit.entries.length === 0 && (
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>-- NOTHING RECORDED --</p>
        )}
        {audit && audit.entries.length > 0 && (
          <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <tbody>
                {audit.entries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: "1px solid var(--gridline)" }}>
                    <td style={{ ...td, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                      {new Date(entry.created_at).toISOString().replace("T", " ").slice(0, 19)}
                    </td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{entry.actor_email}</td>
                    <td style={td}>{entry.action}</td>
                    <td style={{ ...td, color: "var(--text-muted)" }}>
                      {describeDetail(entry.detail)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Share brokering, on the platform page rather than the device page.
 *
 * A platform admin is usually a member of neither side of a share, and the
 * device page's panel is strictly the owning organization's -- so this is
 * where a share between two organizations that cannot see each other gets
 * set up.
 */
function ShareCell({
  device,
  organizations,
  onChange,
}: {
  device: PlatformDevice;
  organizations: PlatformOrg[];
  onChange: () => void;
}) {
  const [list, setList] = useState<DeviceShare[] | null>(null);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [permission, setPermission] = useState<"read" | "control">("read");
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  const count = Number(device.share_count);

  const load = () => {
    platform.deviceShares(device.id).then((r) => setList(r.shares)).catch(() => setList([]));
  };

  // Only when opened. The collapsed state needs a number, and the number
  // arrives with the device list.
  useEffect(() => {
    if (open && list === null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A panel that overlays the table has to be dismissable without choosing
  // anything, or it traps the reader behind a form they only wanted to read.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (!cellRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const add = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await platform.share(device.id, { tenant_id: target, permission, alerts_enabled: alertsEnabled });
      setTarget("");
      load();
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (share: DeviceShare) => {
    if (!confirm(`Stop sharing ${device.name} with ${share.tenant_name}?`)) return;
    setBusy(true);
    try {
      await platform.unshare(device.id, share.id);
      load();
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const available = organizations.filter(
    (o) => o.id !== device.tenant_id && !(list ?? []).some((share) => share.tenant_id === o.id),
  );

  return (
    <div ref={cellRef} style={{ position: "relative", fontSize: 11 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          ...smallButton,
          padding: "2px 6px",
          color: count > 0 ? "var(--text-primary)" : "var(--text-secondary)",
          borderColor: count > 0 ? "var(--border-bright)" : "var(--border)",
          // Load-bearing: the label is padded to a fixed width, and `nowrap`
          // collapses a run of spaces exactly as `normal` does. Without this
          // the padding is written and then thrown away, which is precisely
          // how the status badge shipped with a moving bracket.
          whiteSpace: "pre",
        }}
        aria-expanded={open}
      >
        [ {shareLabel(count)} ]
      </button>

      {open && (
        /*
         * Absolutely positioned so opening it cannot change the row's height.
         * The old cell grew a line per share and a button below them, which is
         * what made the table ragged: rows were different heights according to
         * something nobody was reading most of the time.
         */
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 20,
            minWidth: 300,
            border: "1px solid var(--border-bright)",
            background: "var(--surface-raised)",
            padding: 10,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.6)",
          }}
        >
          <div style={{ color: "var(--text-muted)", letterSpacing: "0.1em", marginBottom: 8 }}>
            {device.name.toUpperCase()} — SHARED WITH
          </div>

          {list === null && <div style={{ color: "var(--text-muted)" }}>…</div>}
          {list !== null && list.length === 0 && (
            <div style={{ color: "var(--text-muted)", marginBottom: 8 }}>-- NOT SHARED --</div>
          )}

          {(list ?? []).map((share) => (
            <div
              key={share.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "3px 0",
                color: "var(--text-secondary)",
                whiteSpace: "nowrap",
              }}
            >
              <span>
                {share.tenant_name}{" "}
                <span style={{ color: "var(--text-muted)" }}>
                  {share.permission}
                  {share.alerts_enabled ? " +alerts" : ""}
                </span>
              </span>
              <button
                disabled={busy}
                onClick={() => void remove(share)}
                style={{ ...smallButton, padding: "1px 6px", color: "var(--status-critical)" }}
                title={`Stop sharing with ${share.tenant_name}`}
              >
                x
              </button>
            </div>
          ))}

          {available.length > 0 ? (
            <div
              style={{
                display: "flex",
                gap: 4,
                flexWrap: "wrap",
                alignItems: "center",
                marginTop: 10,
                paddingTop: 10,
                borderTop: "1px solid var(--gridline)",
              }}
            >
              <select value={target} onChange={(e) => setTarget(e.target.value)} style={select}>
                <option value="">-- ORG --</option>
                {available.map((o) => (
                  <option key={o.id} value={o.id}>{o.name.toUpperCase()}</option>
                ))}
              </select>
              <select
                value={permission}
                onChange={(e) => setPermission(e.target.value as "read" | "control")}
                style={select}
              >
                <option value="read">READ</option>
                <option value="control">CONTROL</option>
              </select>
              <label style={{ display: "flex", gap: 3, alignItems: "center", color: "var(--text-muted)" }}>
                <input
                  type="checkbox"
                  checked={alertsEnabled}
                  onChange={(e) => setAlertsEnabled(e.target.checked)}
                />
                alerts
              </label>
              <button disabled={!target || busy} onClick={() => void add()} style={smallButton}>
                [ ADD ]
              </button>
            </div>
          ) : (
            list !== null && (
              <div style={{ color: "var(--text-muted)", marginTop: 10 }}>
                Shared with every other organization.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function describeDetail(detail: Record<string, unknown>): string {
  const name = typeof detail["deviceName"] === "string" ? detail["deviceName"] : null;
  const from = typeof detail["fromTenantName"] === "string" ? detail["fromTenantName"] : null;
  const to = typeof detail["toTenantName"] === "string" ? detail["toTenantName"] : null;
  if (name && from && to) return `${name}: ${from} → ${to}`;
  return JSON.stringify(detail).slice(0, 120);
}

const heading: React.CSSProperties = { fontSize: 15, margin: "0 0 6px", letterSpacing: "0.14em" };
const help: React.CSSProperties = { color: "var(--text-muted)", fontSize: 11, margin: "0 0 12px", lineHeight: 1.5 };
const card: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-1)", padding: 14 };
const th: React.CSSProperties = { padding: "10px 14px", color: "var(--text-muted)", fontWeight: 500 };
const td: React.CSSProperties = { padding: "9px 14px" };
const select: React.CSSProperties = {
  padding: "5px 7px", fontSize: 11, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)", letterSpacing: "0.06em",
};
const smallButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 9px",
  fontSize: 11, cursor: "pointer", letterSpacing: "0.08em",
};
